import styled from "@emotion/styled";

import type {
  FontSizeProps,
  SpaceProps,
  FontWeightProps,
  LineHeightProps,
  ThemeValue,
  ColorProps,
} from "styled-system";
import {
  space,
  fontSize,
  fontWeight,
  color,
  lineHeight,
  variant,
} from "styled-system";

color.config = {
  ...color.config,
  color: {
    property: "color",
    scale: "colorsV2",
  },
};

import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { type ForwardedRef, forwardRef, type PropsWithChildren } from "react";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";

type TextSkin = "default" | "white";

interface VariantProps {
  variant?: ThemeValue<"textV2", ExactTheme>;
  skin?: TextSkin;
}

type Theme = Omit<ExactTheme, "buttons">;

interface TextV2StyledSystemProps
  extends SpaceProps<Theme>,
    FontSizeProps<Theme>,
    FontWeightProps<Theme>,
    ColorProps<Theme, ThemeValue<"colorsV2", Theme>>,
    LineHeightProps<Theme>,
    VariantProps {
  isLoading?: boolean;
}

export type TextV2Props = PropsWithChildren<TextV2StyledSystemProps>;

const textV2Theme = (theme: ExactTheme) => ({
  default: {
    color: theme.colorsV2.fg.default,
  },
  white: {
    color: theme.colorsV2.white,
  },
});

export const StyledTextV2 = styled("span", {
  shouldForwardProp: (prop) => !["color", "skin", "variant"].includes(prop),
})<TextV2Props>`
  ${variant({
    scale: "textV2",
  })}
  ${({ theme }) =>
    variant({
      prop: "skin",
      variants: textV2Theme(theme),
    })}
    ${color}
    ${space}
    ${fontSize}
    ${fontWeight}
    ${lineHeight}
`;

const TextV2Impl = ((
  {
    isLoading = false,
    variant = "body2",
    skin = "default",
    ...rest
  }: TextV2Props,
  ref: ForwardedRef<HTMLSpanElement>,
) => {
  return (
    <Skeleton isLoading={isLoading}>
      {/*@ts-ignore*/}
      <StyledTextV2 variant={variant} skin={skin} {...rest} ref={ref} />
    </Skeleton>
  );
}) as typeof StyledTextV2;

export const TextV2 = forwardRef(
  // @ts-ignore
  TextV2Impl,
) as unknown as typeof TextV2Impl;
